import { ArrowRight, Check, X, Sparkles } from "lucide-react";
import { SIGNUP_URL, SIGNUP_URL_SMART, SIGNUP_URL_GENIUS } from "@/components/landing/constants";

/* Tier map (post-launch alignment with production app):
   - Free: photo + manual + recent-meals logging + telehealth (no barcode, no voice, no MIQ score, no wearables)
   - Smart $4.99/mo: + barcode/label scan, voice logging (5/day, 50+ langs), SmartGoals,
                     Daily Check-In, micronutrients, hydration, Health Intelligence,
                     MIQ Smart Coach (10/day), Telehealth
   - Genius $9.99/mo: EVERYTHING — Live MIQ score, Bloodwork OCR, Wearables, unlimited voice,
                      unlimited Smart Coach, SmartMeals, SmartSwaps, Recipe Gen,
                      Restaurant-aware AI, AI workout/rehab planner, Weekly Brief,
                      Peptide intelligence, Adaptive plans, Fasting */
const tiers = [
  {
    name: "Free",
    price: "$0",
    period: "",
    annualNote: "Always free. No credit card.",
    tagline: "Basic tracking. Photo + manual logging.",
    color: "#00E5A0",
    colorClass: "text-brand-free",
    borderClass: "border-brand-free/30",
    featured: false,
    ctaUrl: SIGNUP_URL,
    includes: [
      "Photo AI plate scan",
      "Manual meal logging",
      "Recent-meals quick log",
      "Today's macros dashboard",
      "Meal history",
      "Telehealth — on-demand licensed clinicians",
    ],
    excludes: [
      "Barcode + nutrition label scan",
      "MIQ score",
      "Wearable integrations",
    ],
    ctaLabel: "Start free",
  },
  {
    name: "Smart",
    price: "$4.99",
    period: "/mo",
    annualNote: "or $41.99/yr — save 30%",
    tagline: "For daily users who want to understand their food.",
    color: "#3B82F6",
    colorClass: "text-brand-smart",
    borderClass: "border-brand-smart/40",
    featured: false,
    ctaUrl: SIGNUP_URL_SMART,
    includes: [
      "Everything in Free",
      "Barcode + nutrition label scan",
      "🎙️ Voice meal logging (50+ languages, 5/day)",
      "SmartGoals personalized tracking",
      "Daily Check-In (sleep · stress · energy · weight)",
      "10+ micronutrient intelligence",
      "Hydration tracking",
      "Health Intelligence — daily AI insights",
      "MIQ Smart Coach — ask anything (10/day)",
      "Telehealth — on-demand licensed clinicians",
    ],
    excludes: [
      "MIQ score",
      "Wearable integrations",
      "Bloodwork OCR",
    ],
    ctaLabel: "Get Smart",
  },
  {
    name: "Genius",
    price: "$9.99",
    period: "/mo",
    annualNote: "or $83.99/yr — save 30%",
    tagline: "For people who measure to improve. Labs in. Wearables in.",
    color: "#A855F7",
    colorClass: "text-brand-genius",
    borderClass: "border-brand-genius/50",
    featured: true,
    ctaUrl: SIGNUP_URL_GENIUS,
    includes: [
      "Everything in Smart",
      "Live MIQ score (0–100) + MIQ Profile",
      "🩸 Bloodwork & Biomarker OCR — 15+ markers, +15% MIQ Confidence",
      "⌚ Wearable integrations (Apple Health, Google Fit, Whoop, Oura, Garmin, Fitbit, Samsung)",
      "🎙️ Unlimited voice logging (50+ languages)",
      "Unlimited Smart Coach Q&A",
      "SmartMeals — 94% Perfect Fit matching",
      "SmartSwaps for goal optimization",
      "AI Recipe Generator from your kitchen",
      "Restaurant-aware AI (\"what should I eat at Chipotle?\")",
      "Personalized AI workout & rehab planner",
      "Weekly Intelligence Brief",
      "Peptide & supplement intelligence",
      "Adaptive meal + training plans",
      "Fasting & eating windows",
    ],
    excludes: [],
    ctaLabel: "Start 7-day trial",
  },
];

export default function Pricing() {
  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-28 md:py-36 bg-[#070707] border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.24em] text-brand-accent font-bold mb-5">Pricing</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
            Three tiers. <span className="text-gradient-hero">One brain.</span>
          </h2>
          <p className="mt-6 text-white/65 text-base sm:text-lg leading-relaxed font-light">
            Start free with basic tracking + telehealth. Upgrade to Smart to truly understand your food.
            Go Genius for the full MIQ engine, bloodwork OCR, and wearables.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-3 gap-6 items-stretch">
          {tiers.map((t) => (
            <div
              key={t.name}
              data-testid={`pricing-card-${t.name.toLowerCase()}`}
              className={`relative rounded-3xl p-8 flex flex-col ${
                t.featured
                  ? "glass-strong border-2 border-brand-genius/40 shadow-[0_0_60px_rgba(168,85,247,0.25)]"
                  : `glass border ${t.borderClass}`
              }`}
            >
              {t.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-brand-genius/15 border border-brand-genius/40 backdrop-blur-md rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold text-brand-genius">
                  <Sparkles className="w-3 h-3" />
                  Includes MIQ + Bloodwork
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className={`font-display font-bold text-xl tracking-tight ${t.colorClass}`}>{t.name}</div>
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: t.color, boxShadow: `0 0 16px ${t.color}` }}
                />
              </div>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="font-display font-black text-5xl tracking-tighter">{t.price}</span>
                {t.period && <span className="text-white/50 text-sm">{t.period}</span>}
              </div>
              <p data-testid={`pricing-annual-${t.name.toLowerCase()}`} className="mt-1 text-xs text-white/45 font-mono">
                {t.annualNote}
              </p>
              <p className="mt-3 text-white/65 text-sm">{t.tagline}</p>

              <ul className="mt-7 space-y-2.5 flex-1">
                {t.includes.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-white/85 leading-snug">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${t.colorClass}`} />
                    <span>{f}</span>
                  </li>
                ))}
                {t.excludes.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-white/35 leading-snug">
                    <X className="w-4 h-4 mt-0.5 shrink-0" />
                    <span className="line-through decoration-white/15">{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href={t.ctaUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                className={`pill mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 font-semibold text-sm transition-colors ${
                  t.featured
                    ? "bg-white text-black hover:bg-white/90"
                    : "bg-white/5 text-white hover:bg-white/10 border border-white/10"
                }`}
              >
                {t.ctaLabel}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          ))}
        </div>

        <p className="mt-8 text-center text-[11px] text-white/40 max-w-4xl mx-auto leading-relaxed">
          Smart $4.99/mo or $41.99/yr (save 30%) · Genius $9.99/mo or $83.99/yr (save 30%) · MIQ score, bloodwork OCR, and wearable sync are Genius-tier features · Telehealth on every tier · Free forever, no credit card required.
        </p>
      </div>
    </section>
  );
}
